import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { queryClient } from '@/api/queryClient';
import { flushWrites, isWritePending } from '@/state/writeQueue';
import { useBrainStore, type ServerTruth } from '@/state/brainStore';

/**
 * Client-side half of the /ws outbox stream (see
 * apps/api/src/ws/outbox-stream.ts for the server protocol). Turns outbox
 * events into query invalidations so every connected user's calendar /
 * availability views refetch live, without the client needing to know
 * anything about outbox internals beyond {topic, payload}.
 */

// Topics whose writes can change the calendar/availability views. NOT
// confirmationCalls — neither view contains it, and it carries no studioId, so
// including it here would broad-invalidate every calendar/availability query
// network-wide on each confirmation-queue write for no data change. It feeds the
// Confirmations view only, via CONFIRMATIONS_VIEW_TOPICS above.
export const VIEW_TOPICS = new Set(['bookings', 'deposits', 'waivers', 'overrideRequests']);

export interface OutboxFrame {
  kind: 'catchup' | 'event' | 'live';
  id?: string;
  topic?: string;
  payload?: { id: string; studioId?: string };
  createdAt?: string;
  now?: string;
  /** On the 'live' marker: the socket's cursor after catch-up (B2). */
  head?: string;
}

/**
 * Invalidate without cancelling a refetch already in flight (B2: N3, N11, 7kv7).
 * The default `cancelRefetch: true` restarts the fetch on every frame, so under a
 * steady stream a heavy view (Ops, Floor, Confirmations) never finished loading
 * and its screen stayed on stale data.
 */
export const NO_CANCEL = { cancelRefetch: false } as const;

/**
 * Topics that also feed the Deposits Ledger's network-wide view: a booking
 * placement can flip a deposit's row (booking column, "awaiting placement"
 * CTA), so both topics — not just 'deposits' — invalidate it.
 */
export const DEPOSITS_VIEW_TOPICS = new Set(['deposits', 'bookings']);

/**
 * Topics that also feed the Confirmations screens' persona-studio-scoped
 * view: a booking write can flip its cadence lane, a deposit write can flip
 * the top-up track, a waiver write changes readiness, and a confirmationCall
 * write/delete is the queue itself. confirmationCalls carries no studioId, so
 * — like the deposits ledger above — this is always a broad invalidation
 * rather than one scoped to a single studio's query key.
 */
export const CONFIRMATIONS_VIEW_TOPICS = new Set(['bookings', 'deposits', 'waivers', 'confirmationCalls']);

/**
 * Topics that also feed the Ops worklist's network-wide, multi-domain view
 * (tv-1vh.12): bookings/overrideRequests/topupPlans/noRebookFlags/
 * payoutRecords/noDepositDeclineFlags are the worklist's own feeders;
 * confirmationCalls is included too — the Ops worklist's confirmations tab
 * (buildOpsWorklist's `confirmations` section) drains the same escalated-
 * booking signal as the Confirmations screens, so a confirmationCall write
 * can flip it. None of these carry a studioId the Ops view's query key could
 * scope on (it is 'all'-or-persona-studios, not per-studio like calendar/
 * availability), so — like the deposits ledger and confirmations queue above
 * — this is always a broad invalidation.
 */
export const OPS_VIEW_TOPICS = new Set([
  'bookings',
  'overrideRequests',
  'topupPlans',
  'noRebookFlags',
  'payoutRecords',
  'noDepositDeclineFlags',
  'confirmationCalls',
  // W5: the unpaid-links (deposits), Meta-unanswered (customers) and pay-by
  // extension feeders.
  'deposits',
  'customers',
  'paybyExtensionRequests',
]);

/**
 * Topics that also feed the Floor desk's network-wide view (tv-1vh.12):
 * bookings/deposits/waivers/pieces/salePayments/merchItems are the Floor
 * desk's own feeders (deposits/pieces are read transitively via
 * booking.depositId/pieceId, but a plain deposit/piece write still needs to
 * refresh the view since it changes the attached record's content, not just
 * which bookings reference it); confirmationCalls is included too — a
 * running-late arrival's Contact flow (ensureConfirmationCall) mints one from
 * the Floor screen itself. None of these carry a studioId the Floor view's
 * query key could scope on (it is 'all'-or-persona-studios, not per-studio
 * like calendar/availability), so — like the deposits ledger/confirmations
 * queue/Ops worklist above — this is always a broad invalidation. Deliberately
 * NOT added to the calendar/availability VIEW_TOPICS below — pieces/
 * salePayments/merchItems play no part in those views.
 */
export const FLOOR_VIEW_TOPICS = new Set([
  'bookings',
  'deposits',
  'waivers',
  'pieces',
  'salePayments',
  'merchItems',
  'confirmationCalls',
]);

/**
 * The roster view (leaderboard + availability heatmap) is bookings-only over the
 * full network, so only a booking write can change it (artists/studios are
 * reference data, not written through the emitting path). One broad invalidation
 * — the roster query has no per-studio key (tv-ods.3).
 */
export const ROSTER_VIEW_TOPICS = new Set(['bookings']);

/**
 * The messenger view (live reads over customers + their sequence state) is fed
 * only by writes to those two domains — a customer write changes contact/thread
 * data, a sequenceState write moves a conversation along its cadence. Neither
 * carries a studioId the messenger query key could scope on, so this is one
 * broad invalidation of the single ['messenger'] query (tv-ods.3).
 */
export const MESSENGER_VIEW_TOPICS = new Set(['customers', 'sequenceState']);

/**
 * Pure, side-effect-free key derivation: the query keys an outbox frame affects
 * — the network-wide keys whose topic set contains `evt.topic`, plus (when the
 * topic is a calendar/availability view topic) the studio-scoped calendar +
 * availability keys, scoped to `payload.studioId` when present. Returns [] for a
 * topic-less or non-view frame. This is the single mapping handleOutboxEvent
 * feeds into the coalescer, kept pure so it is trivially testable.
 */
export function keysForEvent(evt: OutboxFrame): QueryKey[] {
  const keys: QueryKey[] = [];
  if (!evt.topic) return keys;

  // Network-wide views (no per-studio query key): a single broad invalidation
  // regardless of which studio the event came from.
  if (DEPOSITS_VIEW_TOPICS.has(evt.topic)) keys.push(['deposits']);
  if (CONFIRMATIONS_VIEW_TOPICS.has(evt.topic)) keys.push(['confirmations']);
  if (OPS_VIEW_TOPICS.has(evt.topic)) keys.push(['ops']);
  if (FLOOR_VIEW_TOPICS.has(evt.topic)) keys.push(['floor']);
  if (ROSTER_VIEW_TOPICS.has(evt.topic)) keys.push(['roster']);
  if (MESSENGER_VIEW_TOPICS.has(evt.topic)) keys.push(['messenger']);

  // Studio-scoped calendar/availability views.
  if (VIEW_TOPICS.has(evt.topic)) {
    const studioId = evt.payload?.studioId;
    if (studioId) {
      keys.push(['calendar', studioId]);
      keys.push(['availability', studioId]);
      // The all-studios calendar (75n8) holds every studio's records.
      keys.push(['calendar', 'all']);
    } else {
      keys.push(['calendar']);
      keys.push(['availability']);
    }
  }

  return keys;
}

/**
 * Coalescing window. A burst of /ws frames (the real-world shape: several
 * concurrent sessions writing at once) used to fan out into ONE invalidate —
 * and therefore one view refetch — PER FRAME (the STORM, tv-4jm). We instead
 * collect the affected keys into a pending set, deduped by a stable
 * serialization, and fire ONE invalidate per unique key on a trailing debounce
 * (COALESCE_MS, restarted on each new frame) with a max-wait cap
 * (COALESCE_MAX_MS) so a sustained stream still flushes at least that often.
 * Mirrors the debounce+max-wait shape of state/writeQueue.ts.
 */
export const COALESCE_MS = 50;
export const COALESCE_MAX_MS = 250;

const pending = new Map<string, QueryKey>();
/** Customers a frame in this window said changed; read and folded into the store on flush (B2). */
const pendingCustomers = new Set<string>();
let coalesceQc: QueryClient | null = null;
let coalesceIdleTimer: ReturnType<typeof setTimeout> | null = null;
let coalesceMaxTimer: ReturnType<typeof setTimeout> | null = null;

/** Invalidate every pending key once, then clear the window. */
function flushInvalidations(): void {
  if (coalesceIdleTimer) { clearTimeout(coalesceIdleTimer); coalesceIdleTimer = null; }
  if (coalesceMaxTimer) { clearTimeout(coalesceMaxTimer); coalesceMaxTimer = null; }
  const qc = coalesceQc;
  const keys = [...pending.values()];
  pending.clear();
  const customerIds = [...pendingCustomers];
  pendingCustomers.clear();
  if (customerIds.length > 0) refreshCustomers(customerIds);
  if (!qc) return;
  for (const queryKey of keys) {
    void qc.invalidateQueries({ queryKey }, NO_CANCEL);
  }
}

/**
 * Turn an outbox frame into query invalidations, coalesced across a burst.
 * Signature kept as `(qc, evt)` so callers/tests still pass the query client;
 * the flush uses the qc from the most recent call (always the singleton
 * queryClient in practice). Invalidation is now DEFERRED to the debounce.
 */
export function handleOutboxEvent(qc: QueryClient, evt: OutboxFrame): void {
  const keys = keysForEvent(evt);
  if (keys.length === 0) return;

  coalesceQc = qc;
  if ((evt.topic === 'customers' || evt.topic === 'sequenceState') && evt.payload?.id) pendingCustomers.add(evt.payload.id);
  for (const key of keys) {
    // Dedupe across frames by a stable serialization: same topic/studio collapse
    // to one entry, different studios serialize differently and both survive.
    pending.set(JSON.stringify(key), key);
  }

  if (coalesceIdleTimer) clearTimeout(coalesceIdleTimer);
  coalesceIdleTimer = setTimeout(flushInvalidations, COALESCE_MS);
  if (!coalesceMaxTimer) coalesceMaxTimer = setTimeout(flushInvalidations, COALESCE_MAX_MS);
}

/**
 * B2 (8e8d, 7gq9): the customer record and My queue read the store, which a view
 * invalidation never reaches, so a server-written message (the day-advance's
 * reminder SMS, another device's text) stayed invisible until a reload. Read the
 * changed customers and fold them in (conversation merged, never lost). A failed
 * read is dropped: the next frame, or a reload, recovers.
 *
 * `dropMovedRows` (the DM link panel's re-read only, tv-isee) also removes
 * server rows the read no longer returns. A frame refresh never does: an inbound
 * DM is stamped with Instagram's send time, so a stale read resolving after a
 * newer one would otherwise drop a real message.
 */
function refreshCustomers(ids: string[], opts: { dropMovedRows?: boolean } = {}): Promise<void> {
  const startedAt = opts.dropMovedRows ? Date.now() : undefined;
  // Loaded lazily: api/views imports the store, which must finish initialising first.
  return import('@/api/views')
    .then(({ fetchCustomersView }) => fetchCustomersView(ids))
    .then(({ customers, sequenceState }) => {
      const store = useBrainStore.getState();
      store.absorbServerCustomers(customers, startedAt);
      // B8: mirror the server's sequence records for these customers (a deleted
      // one = the lead left the sequences) without writing them back.
      if (sequenceState) store.absorbServerSequenceState(ids, sequenceState, (id) => isWritePending('sequenceState', id));
      // tv-isee: a customer the read no longer returns was deleted on the server
      // (a DM lead merged by a link). Drop it without write-through, so a later
      // edit can't PUT it back. A tab's own unsaved create is left alone.
      for (const id of ids) {
        if (!customers[id] && !isWritePending('customers', id)) store.dropServerDeletedCustomer(id);
      }
    })
    .catch(() => undefined);
}

/**
 * Re-read one customer now, outside the coalesced burst, right after a DM link
 * moved its rows (tv-isee): rows the server moved away are dropped too.
 */
export function refreshCustomer(id: string): Promise<void> {
  return refreshCustomers([id], { dropMovedRows: true });
}

/** Test hygiene: drop the pending keys + timers without flushing. */
export function resetLiveStreamCoalescer(): void {
  if (coalesceIdleTimer) { clearTimeout(coalesceIdleTimer); coalesceIdleTimer = null; }
  if (coalesceMaxTimer) { clearTimeout(coalesceMaxTimer); coalesceMaxTimer = null; }
  pending.clear();
  pendingCustomers.clear();
  coalesceQc = null;
}

/**
 * View -> store sync (tv-2na.11.4 / tv-2na.11.5). A /ws frame only invalidates
 * views, so an out-of-band write (a customer signing their waiver or paying a
 * deposit on their own device) lands in the query cache and nowhere else: the
 * Floor showed the waiver signed while the brain-store check-in gate still read
 * it as sent, and a paid booking stayed "pending deposit" on the detail card
 * until a reload. These views carry full records, so each fresh fetch is folded
 * into the store forward-only (brainStore.absorbServerTruthInto).
 */
const STORE_SYNC_VIEW_KEYS: ReadonlySet<string> = new Set(['floor', 'calendar', 'confirmations', 'deposits']);

function truthOf(data: unknown): ServerTruth | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const d = data as ServerTruth;
  return d.bookings || d.deposits || d.waivers || d.salePayments ? d : undefined;
}

/** Fold every cached store-sync view into the brain store now (e.g. at check-in). */
export function absorbCachedViews(qc: QueryClient = queryClient): void {
  for (const q of qc.getQueryCache().getAll()) {
    if (!STORE_SYNC_VIEW_KEYS.has(String(q.queryKey[0]))) continue;
    const truth = truthOf(q.state.data);
    if (truth) useBrainStore.getState().absorbServerTruth(truth);
  }
}

/** Fold each successful store-sync view fetch into the brain store. Returns the disposer. */
export function installViewStoreSync(qc: QueryClient = queryClient): () => void {
  return qc.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success') return;
    if (!STORE_SYNC_VIEW_KEYS.has(String(event.query.queryKey[0]))) return;
    const truth = truthOf(event.query.state.data);
    if (truth) useBrainStore.getState().absorbServerTruth(truth);
  });
}

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;
/**
 * How long a socket must stay open before we trust it enough to reset the
 * reconnect backoff. Resetting on `onopen` (as we used to) means a socket that
 * opens then immediately closes reconnects at INITIAL_BACKOFF_MS forever — a
 * tight reconnect loop that never backs off. Requiring a stable open first
 * preserves geometric backoff across a flapping connection.
 */
export const STABLE_OPEN_MS = 5000;

let started = false;

// Reconnect timers, hoisted to module scope so resetLiveStream() can clear them
// between test runs. These are SEPARATE from the invalidation coalescer's
// idle/max timers above (which resetLiveStreamCoalescer() owns).
let stableTimer: ReturnType<typeof setTimeout> | undefined;
let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

/** Opens the /ws subscription once and wires it into handleOutboxEvent. Reconnects with backoff. */
export function startLiveStream(): void {
  if (started) return;
  if (typeof WebSocket === 'undefined') return;
  started = true;
  installViewStoreSync(queryClient);

  // A fresh tab has just read every view: subscribe at the outbox head rather than
  // replaying the whole outbox (B2: 8e8f, N11). A reconnect resumes after the last
  // id seen, or the head the server reported on 'live'.
  let cursor = 'head';
  let backoff = INITIAL_BACKOFF_MS;

  function connect(): void {
    const protocol = typeof location !== 'undefined' && location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = typeof location !== 'undefined' ? location.host : '';
    const url = `${protocol}//${host}/ws?after=${encodeURIComponent(cursor)}`;

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      scheduleReconnect();
      return;
    }

    socket.onopen = () => {
      flushWrites(); // a reconnected socket means the API is reachable again — drain queued writes
      // Reset backoff ONLY after the socket has proven stable (survived
      // ≥STABLE_OPEN_MS). An open→quick-close never fires this (onclose clears
      // it), so a flapping socket keeps backing off geometrically.
      stableTimer = setTimeout(() => {
        backoff = INITIAL_BACKOFF_MS;
      }, STABLE_OPEN_MS);
    };

    socket.onmessage = (event: MessageEvent) => {
      let frame: unknown;
      try {
        frame = JSON.parse(String(event.data));
      } catch {
        // A malformed frame is dropped, never fatal: don't advance the cursor,
        // reconnect, or throw — any of those would stall or hammer the stream.
        console.warn('[liveStream] dropping unparseable /ws frame');
        return;
      }
      if (!frame || typeof frame !== 'object') return;

      const record = frame as Record<string, unknown>;
      if (record.type === 'ping') {
        socket.send('{"type":"pong"}');
        return;
      }

      if (record.kind === 'live') {
        if (typeof record.head === 'string' && /^\d+$/.test(record.head)) cursor = record.head;
        return;
      }

      if (record.kind === 'catchup' || record.kind === 'event') {
        const evt = record as unknown as OutboxFrame;
        if (typeof evt.id === 'string') cursor = evt.id;
        handleOutboxEvent(queryClient, evt);
      }
    };

    socket.onerror = () => {
      socket.close();
    };

    socket.onclose = () => {
      // Cancel the pending stable-open reset FIRST so an open→quick-close never
      // resets backoff, then reconnect at the (still-growing) current backoff.
      if (stableTimer) {
        clearTimeout(stableTimer);
        stableTimer = undefined;
      }
      scheduleReconnect();
    };
  }

  function scheduleReconnect(): void {
    reconnectTimer = setTimeout(connect, backoff);
    backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
  }

  connect();
}

/**
 * Test hygiene: let startLiveStream run again and clear the pending reconnect
 * timers so a fresh run starts clean. Mirrors resetLiveStreamCoalescer (which
 * owns the SEPARATE invalidation-coalescer timers); a full reset calls both.
 */
export function resetLiveStream(): void {
  started = false;
  if (stableTimer) {
    clearTimeout(stableTimer);
    stableTimer = undefined;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = undefined;
  }
}
